import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';

import { ErrorTypeEnum } from '../../../../main/constants/error-type.ts';
import { TrackingEventEnum } from '../../../../main/constants/tracking-event.ts';
import { ThunkApiConfigType } from '../../../../main/store/main.store.ts';
import { getLocaleUtil } from '../../../../main/utils/locale.util.ts';
import { getSentryUtil } from '../../../../main/utils/monitoring/sentry.util.ts';
import { getToasterUtil } from '../../../../main/utils/toaster.util.ts';
import { getTrackerUtil } from '../../../../main/utils/trackers/tracker.util.ts';

import { updateChildProfileMutationRequest } from '../../../requests/update-child-profile.mutation.ts';
import { ProfileUpdateFormSchema } from '../../../types/accounts-validation.types.ts';

import { ACCOUNTS_SLICE_NAME, AccountsSliceState } from '../accounts.slice.types.ts';

interface Props {
  id: string;
  incorrectHelpThreshold: number;
}

const THUNK_NAME = `${ACCOUNTS_SLICE_NAME}/updateAppSettings`;

const handleError = (input: Props, message?: string): Error => {
  const errorMessage = message ?? getLocaleUtil().select('accounts:update_app_settings_failed');
  getToasterUtil().error(errorMessage);
  const error = new Error(errorMessage);
  error.name = ErrorTypeEnum.UPDATE_APP_SETTINGS_ERROR;

  getSentryUtil().trackError(
    error,
    {
      thunk: THUNK_NAME,
      errorType: ErrorTypeEnum.UPDATE_APP_SETTINGS_ERROR,
    },
    { input }
  );
  getTrackerUtil().track({
    event: TrackingEventEnum.APP_SETTINGS_UPDATE_FAILED,
  });

  return error;
};

export const updateAppSettingsThunk = createAsyncThunk<void, Props, ThunkApiConfigType>(THUNK_NAME, async (props) => {
  const { id, incorrectHelpThreshold } = props;

  const parsedInput = ProfileUpdateFormSchema.safeParse(props);
  if (!parsedInput.success) {
    const error = handleError(props);
    throw error;
  }

  const result = await updateChildProfileMutationRequest(parsedInput.data);
  if (!result?.success) {
    const error = handleError(props, result.message);
    throw error;
  }

  getToasterUtil().success(getLocaleUtil().select('accounts:update_app_settings_success'));
  getTrackerUtil().track({
    event: TrackingEventEnum.APP_SETTINGS_UPDATED,
    data: {
      id,
      incorrectHelpThreshold,
    },
  });
});

export const updateAppSettingsThunkExtraReducers = (builder: ActionReducerMapBuilder<AccountsSliceState>): void => {
  builder.addCase(updateAppSettingsThunk.pending, (state) => {
    state.isUpdateAppSettingsLoading = true;
  });

  builder.addCase(updateAppSettingsThunk.fulfilled, (state, action) => {
    state.isUpdateAppSettingsLoading = false;

    state.availableProfiles = state.availableProfiles?.map((profile) => {
      if (profile.id === action.meta.arg.id) {
        return {
          ...profile,
          incorrectHelpThreshold: action.meta.arg.incorrectHelpThreshold,
        };
      }

      return profile;
    });
    if (state.currentProfile && state.currentProfile.id === action.meta.arg.id) {
      state.currentProfile.incorrectHelpThreshold = action.meta.arg.incorrectHelpThreshold;
    }
  });

  builder.addCase(updateAppSettingsThunk.rejected, (state) => {
    state.isUpdateAppSettingsLoading = false;
  });
};
